import { useState } from "react";
import { systemUsers, systemRoles, type SystemUser, type Role } from "@/mocks/settings";

const permissionLabels: Record<string, string> = {
  "*": "全部权限",
  "profile:view": "查看档案",
  "profile:edit": "编辑档案",
  "sales:view": "查看售气",
  "sales:operate": "售气操作",
  "meter:view": "查看抄表",
  "report:view": "查看报表",
  "alarm:view": "查看告警",
  "alarm:handle": "处理告警",
  "payment:view": "查看支付",
  "payment:operate": "支付操作",
};

const roleColors: Record<string, string> = {
  super_admin: "bg-red-100 text-red-700",
  ops_admin: "bg-orange-100 text-orange-700",
  meter_reader: "bg-blue-100 text-blue-700",
  finance: "bg-green-100 text-green-700",
  safety_officer: "bg-yellow-100 text-yellow-700",
  readonly: "bg-gray-100 text-gray-600",
};

export default function UserPermissions() {
  const [activeTab, setActiveTab] = useState<"users" | "roles">("users");
  const [users, setUsers] = useState<SystemUser[]>(systemUsers);
  const [roles] = useState<Role[]>(systemRoles);
  const [searchText, setSearchText] = useState("");
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", realName: "", role: "readonly", department: "", phone: "", email: "" });

  const filteredUsers = users.filter(u =>
    u.realName.includes(searchText) || u.username.includes(searchText) || u.department.includes(searchText)
  );

  const toggleUserStatus = (id: string) => {
    setUsers(prev => prev.map(u =>
      u.id === id ? { ...u, status: u.status === "启用" ? "禁用" : "启用" } : u
    ));
  };

  const handleAddUser = () => {
    if (!newUser.username || !newUser.realName) return;
    const role = roles.find(r => r.key === newUser.role);
    const user: SystemUser = {
      id: `SU${String(users.length + 1).padStart(3, "0")}`,
      username: newUser.username,
      realName: newUser.realName,
      role: role?.name || "只读用户",
      roleKey: newUser.role,
      department: newUser.department,
      phone: newUser.phone,
      email: newUser.email,
      status: "启用",
      lastLogin: "-",
      createTime: new Date().toISOString().split("T")[0],
      permissions: role?.permissions || [],
    };
    setUsers(prev => [...prev, user]);
    setNewUser({ username: "", realName: "", role: "readonly", department: "", phone: "", email: "" });
    setShowAddUser(false);
  };

  return (
    <div className="space-y-4">
      {/* Tab */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {(["users", "roles"] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab ? "bg-white text-gray-800 font-medium" : "text-gray-500 hover:text-gray-700"}`}
          >
            {tab === "users" ? "系统用户" : "角色权限"}
          </button>
        ))}
      </div>

      {activeTab === "users" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-sm text-gray-400"></i>
              </div>
              <input
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="搜索用户名/姓名/部门"
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 w-64"
              />
            </div>
            <button
              onClick={() => setShowAddUser(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i> 新增用户
            </button>
          </div>

          {/* 新增用户表单 */}
          {showAddUser && (
            <div className="bg-orange-50 border border-[#E8531A]/20 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">新增系统用户</h4>
              <div className="grid grid-cols-3 gap-3 mb-3">
                {[
                  { label: "用户名", key: "username", placeholder: "登录用户名" },
                  { label: "真实姓名", key: "realName", placeholder: "真实姓名" },
                  { label: "部门", key: "department", placeholder: "所属部门" },
                  { label: "手机号", key: "phone", placeholder: "手机号码" },
                  { label: "邮箱", key: "email", placeholder: "邮箱地址" },
                ].map(field => (
                  <div key={field.key}>
                    <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
                    <input
                      value={newUser[field.key as keyof typeof newUser]}
                      onChange={e => setNewUser(prev => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
                    />
                  </div>
                ))}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">角色</label>
                  <select
                    value={newUser.role}
                    onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 cursor-pointer"
                  >
                    {roles.map(r => (
                      <option key={r.key} value={r.key}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleAddUser} className="px-4 py-2 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap">确认添加</button>
                <button onClick={() => setShowAddUser(false)} className="px-4 py-2 bg-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-300 cursor-pointer whitespace-nowrap">取消</button>
              </div>
            </div>
          )}

          {/* 用户列表 */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {["用户名", "真实姓名", "角色", "部门", "最后登录", "状态", "操作"].map(h => (
                    <th key={h} className="text-left text-xs text-gray-500 font-medium px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#E8531A]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-[#E8531A]">{user.realName[0]}</span>
                        </div>
                        <span className="text-sm text-gray-700">{user.username}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">{user.realName}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColors[user.roleKey] || "bg-gray-100 text-gray-600"}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{user.department}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">{user.lastLogin}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${user.status === "启用" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap"
                        >
                          详情
                        </button>
                        <button
                          onClick={() => toggleUserStatus(user.id)}
                          className={`text-xs cursor-pointer whitespace-nowrap ${user.status === "启用" ? "text-red-500 hover:underline" : "text-green-600 hover:underline"}`}
                        >
                          {user.status === "启用" ? "禁用" : "启用"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "roles" && (
        <div className="grid grid-cols-2 gap-4">
          {roles.map(role => (
            <div
              key={role.id}
              onClick={() => setSelectedRole(selectedRole?.id === role.id ? null : role)}
              className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${selectedRole?.id === role.id ? "border-[#E8531A]/40 bg-orange-50/30" : "border-gray-100 hover:border-gray-200"}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColors[role.key] || "bg-gray-100 text-gray-600"}`}>
                      {role.name}
                    </span>
                    <span className="text-xs text-gray-400">{role.userCount} 人</span>
                  </div>
                  <p className="text-xs text-gray-500">{role.description}</p>
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${selectedRole?.id === role.id ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400`}></i>
                </div>
              </div>
              {selectedRole?.id === role.id && (
                <div className="border-t border-gray-100 pt-3">
                  <div className="text-xs text-gray-500 mb-2">权限列表</div>
                  <div className="flex flex-wrap gap-1.5">
                    {role.permissions.map((perm, i) => (
                      <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {permissionLabels[perm] || perm}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 用户详情弹窗 */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setSelectedUser(null)}>
          <div className="bg-white rounded-2xl p-6 w-[480px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800">用户详情</h3>
              <button onClick={() => setSelectedUser(null)} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-full cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="flex items-center gap-4 mb-5 p-4 bg-gray-50 rounded-xl">
              <div className="w-14 h-14 rounded-full bg-[#E8531A]/10 flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-bold text-[#E8531A]">{selectedUser.realName[0]}</span>
              </div>
              <div>
                <div className="text-base font-semibold text-gray-800">{selectedUser.realName}</div>
                <div className="text-sm text-gray-500">@{selectedUser.username}</div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium mt-1 inline-block ${roleColors[selectedUser.roleKey] || "bg-gray-100 text-gray-600"}`}>
                  {selectedUser.role}
                </span>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              {[
                { label: "部门", value: selectedUser.department },
                { label: "手机", value: selectedUser.phone },
                { label: "邮箱", value: selectedUser.email },
                { label: "状态", value: selectedUser.status },
                { label: "最后登录", value: selectedUser.lastLogin },
                { label: "创建时间", value: selectedUser.createTime },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-sm text-gray-400 w-20">{item.label}</span>
                  <span className="text-sm text-gray-700">{item.value}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-2">权限列表</div>
              <div className="flex flex-wrap gap-1.5">
                {selectedUser.permissions.map((perm, i) => (
                  <span key={i} className="px-2 py-0.5 bg-orange-50 text-[#E8531A] text-xs rounded-full border border-[#E8531A]/20">
                    {permissionLabels[perm] || perm}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
